import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthFrame from "@/components/AuthFrame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);

  const onChange = (key) => (e) => {
    setPendingVerification(false);
    setForm({ ...form, [key]: e.target.value });
  };

  const goHome = () => {
    const inviteToken = new URLSearchParams(window.location.search).get("invite");
    navigate(inviteToken ? `/team?invite=${encodeURIComponent(inviteToken)}` : "/");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.requires_verification) {
        setPendingVerification(true);
        toast.success("Controleer je mail");
        return;
      }
      toast.success("Welkom terug");
      goHome();
    } catch (err) {
      toast.error(err.response?.data?.detail || "E-mailadres of wachtwoord klopt niet");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFrame
      title="Inloggen"
      subtitle="Gebruik je e-mailadres en wachtwoord."
      heroTitle="Inloggen en door."
      heroText="Je sessie blijft bewaard in de app, zodat je bij de kast meteen verder kunt."
      footer={
        <>
          Nog geen account?{" "}
          <Link to="/register" className="font-semibold text-amber-700 hover:text-amber-800" data-testid="link-register">
            Account aanmaken
          </Link>
          <span className="mx-2 text-zinc-300">·</span>
          <Link to="/welcome" className="font-semibold text-zinc-600 hover:text-zinc-800">Terug</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-semibold text-zinc-800">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={form.email}
                onChange={onChange("email")}
                className="h-[52px] rounded-xl text-base bg-white border-zinc-200 shadow-sm"
                autoComplete="email"
                required
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="password" className="text-sm font-semibold text-zinc-800">Wachtwoord</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-amber-700 hover:text-amber-800">
                  Wachtwoord vergeten?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={form.password}
                onChange={onChange("password")}
                className="h-[52px] rounded-xl text-base bg-white border-zinc-200 shadow-sm"
                autoComplete="current-password"
                required
              />
            </div>

            {pendingVerification && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                <div className="text-sm font-bold text-amber-950">Controleer je mail</div>
                <div className="text-xs text-amber-800/80 mt-0.5">
                  Open de bevestigingslink om je account in de app te activeren.
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-[52px] rounded-xl text-base bg-amber-600 hover:bg-amber-700 font-bold btn-press shadow-sm shadow-amber-600/20"
            >
              {loading ? "Bezig..." : "Inloggen"}
            </Button>
          </form>
    </AuthFrame>
  );
}
